import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import StorageIcon from '@mui/icons-material/Storage';
import api from '../services/api';

const TripProgress = ({ visible, onComplete }) => {
  const [progress, setProgress] = useState({
    status: 'idle',
    total_steps: 7,
    current_step: 0,
    message: 'Waiting to start...',
    cache_hits: 0,
    cache_misses: 0,
    cache_size: 0
  });
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);

  // Calculate progress percentage
  const calculateProgress = () => {
    if (progress.total_steps === 0) return 0;
    return Math.min(100, Math.round((progress.current_step / progress.total_steps) * 100));
  };

  // Reset progress state when component becomes visible again
  useEffect(() => {
    if (visible) {
      // Reset to initial state
      setProgress({
        status: 'idle',
        total_steps: 7,
        current_step: 0,
        message: 'Waiting to start...',
        cache_hits: 0,
        cache_misses: 0,
        cache_size: 0
      });
      setError(null);
    }
  }, [visible]);

  // Start polling for progress updates when visible
  useEffect(() => {
    let intervalId = null;
    
    const fetchProgressWrapper = async () => {
      try {
        // Use our API service with authentication
        const response = await api.get('/trip-progress');
        setProgress(response.data);
      } catch (err) {
        console.error("Progress fetch error:", err);
        if (err.response && err.response.status === 401) {
          setError("Authentication error. Please refresh the page and login again.");
        } else {
          setError(`Failed to fetch progress: ${err.message}`);
        }
        setPolling(false);
      }
    };
    
    if (visible) {
      console.log("Progress polling started");
      setPolling(true);
      // Fetch immediately before starting interval
      fetchProgressWrapper();
      intervalId = setInterval(fetchProgressWrapper, 500); // Poll every 500ms
    } else {
      console.log("Progress polling stopped");
      setPolling(false);
      if (intervalId) clearInterval(intervalId);
    }
    
    return () => {
      if (intervalId) {
        console.log("Cleaning up progress polling interval");
        clearInterval(intervalId);
      }
    };
  }, [visible]);
  
  // Check if we should stop polling when progress is complete
  useEffect(() => {
    if (progress.status === 'complete' && polling) {
      setPolling(false);
      if (onComplete) onComplete();
    }
  }, [progress.status, polling, onComplete]);

  // fetchProgress is now included directly in the useEffect to avoid dependency issues

  if (!visible) return null;
  
  return (
    <Box sx={{ 
      width: '100%', 
      p: 3, 
      borderRadius: 2, 
      bgcolor: 'background.paper',
      boxShadow: 2,
      mb: 3
    }}>
      <Typography variant="h6" gutterBottom>
        Trip Generation Progress
      </Typography>
      
      {error ? (
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
      ) : (
        <>
          {/* Status and message */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ minWidth: 35, mr: 1 }}>
              {progress.status === 'complete' ? (
                <CloudDoneIcon color="success" />
              ) : (
                <CircularProgress size={20} />
              )}
            </Box>
            <Typography variant="body1">
              {progress.message}
            </Typography>
          </Box>
          
          {/* Progress bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={calculateProgress()} 
                color={progress.status === 'complete' ? 'success' : 'primary'}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {`${calculateProgress()}%`}
              </Typography>
            </Box>
          </Box>
          
          {/* Cache and cost statistics */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip 
              icon={<StorageIcon />} 
              label={`Cached Distances: ${progress.cache_size?.toLocaleString() || 0}`} 
              variant="outlined"
              color="secondary"
              size="small"
            />
            {progress.cache_hits > 0 && (
              <Chip 
                label={`Cache Hits: ${progress.cache_hits?.toLocaleString()}`} 
                variant="outlined"
                color="success"
                size="small"
              />
            )}
            {progress.combinations_tried > 0 && (
              <Chip 
                label={`Combinations: ${progress.combinations_tried?.toLocaleString()}`} 
                variant="outlined"
                color="primary"
                size="small"
              />
            )}
          </Box>
          
          {/* Step information */}
          {progress.status !== 'idle' && progress.status !== 'complete' && (
            <Box sx={{ mt: 2, bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                Step {progress.current_step} of {progress.total_steps}: 
                {progress.status === 'optimizing' ? ' Finding optimal route combinations...' : 
                 progress.status === 'calculating' ? ' Calculating distances...' : 
                 progress.status === 'loading' ? ' Loading data...' : 
                 ' Processing...'}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TripProgress;